import { useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Pencil, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const EMPTY_FORM = {
  codigo: "",
  nome: "",
  setor: "",
  salario: "",
  descritivo: "",
  descritivo_beneficios: "",
  ativo: true,
};

export default function CargosESalarios() {
  const [search, setSearch] = useState("");
  const [filterAtivo, setFilterAtivo] = useState<boolean | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [emitenteIdFiltro, setEmitenteIdFiltro] = useState<number | undefined>(undefined);
  const [formEmitenteId, setFormEmitenteId] = useState<number | null>(null);
  const { user } = useGoogleAuth();

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, {
    enabled: !!user && user.role !== "cliente",
  });

  const emitenteIdConsulta = user?.role === "cliente"
    ? user.emitenteId ?? undefined
    : emitenteIdFiltro;

  const { data: cargos = [], refetch } = trpc.rh.cargos.list.useQuery(
    { search: search || undefined, ativo: filterAtivo, emitenteIdFiltro: emitenteIdConsulta },
    { enabled: !!user }
  );

  const createMut = trpc.rh.cargos.create.useMutation({
    onSuccess: () => { toast.success("Cargo criado"); refetch(); fecharModal(); },
    onError: (e) => toast.error(e.message),
  });

  const updateMut = trpc.rh.cargos.update.useMutation({
    onSuccess: () => { toast.success("Cargo atualizado"); refetch(); fecharModal(); },
    onError: (e) => toast.error(e.message),
  });

  function abrirNovo() {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setFormEmitenteId(user?.emitenteId ?? null);
    setModalOpen(true);
  }

  function abrirEditar(cargo: any) {
    setEditId(cargo.id);
    setForm({
      codigo: cargo.codigo ?? "",
      nome: cargo.nome ?? "",
      setor: cargo.setor ?? "",
      salario: cargo.salario != null ? String(cargo.salario) : "",
      descritivo: cargo.descritivo ?? "",
      descritivo_beneficios: cargo.descritivo_beneficios ?? "",
      ativo: cargo.ativo,
    });
    setFormEmitenteId(cargo.emitenteId ?? null);
    setModalOpen(true);
  }

  function fecharModal() {
    setModalOpen(false);
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setFormEmitenteId(null);
  }

  function handleSalvar() {
    const emitenteId = user?.role === "cliente" ? user.emitenteId ?? null : formEmitenteId;
    if (!emitenteId) return toast.error("Selecione um emitente");
    if (!form.nome.trim()) return toast.error("Nome é obrigatório");
    const salario = form.salario !== "" ? parseFloat(form.salario) : undefined;
    if (form.salario !== "" && isNaN(salario!)) return toast.error("Salário inválido");

    const payload = {
      codigo: form.codigo || undefined,
      nome: form.nome.trim(),
      setor: form.setor || undefined,
      salario,
      descritivo: form.descritivo || undefined,
      descritivo_beneficios: form.descritivo_beneficios || undefined,
      ativo: form.ativo,
    };

    if (editId !== null) {
      updateMut.mutate({ id: editId, ...payload });
    } else {
      createMut.mutate({ ...payload, emitenteId });
    }
  }

  const isBusy = createMut.isPending || updateMut.isPending;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cargos e Salários</h1>
          <p className="text-sm text-muted-foreground">{cargos.length} cargo{cargos.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={abrirNovo}>
          <Plus className="w-4 h-4 mr-2" /> Novo Cargo
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por nome, código ou setor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {user?.role !== "cliente" && (
          <Select
            value={emitenteIdFiltro ? String(emitenteIdFiltro) : "todos"}
            onValueChange={(value) => setEmitenteIdFiltro(value === "todos" ? undefined : Number(value))}
          >
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Todos os emitentes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os emitentes</SelectItem>
              {emitentes.map((emitente: any) => (
                <SelectItem key={emitente.id} value={String(emitente.id)}>
                  {emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant={filterAtivo === undefined ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterAtivo(undefined)}
          >Todos</Button>
          <Button
            variant={filterAtivo === true ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterAtivo(true)}
          >Ativos</Button>
          <Button
            variant={filterAtivo === false ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterAtivo(false)}
          >Inativos</Button>
        </div>
      </div>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead>Salário</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(cargos as any[]).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Nenhum cargo encontrado
                </TableCell>
              </TableRow>
            ) : (
              (cargos as any[]).map((cargo) => (
                <TableRow key={cargo.id}>
                  <TableCell className="font-mono text-sm">{cargo.codigo ?? "—"}</TableCell>
                  <TableCell className="font-medium">{cargo.nome}</TableCell>
                  <TableCell>{cargo.setor ?? "—"}</TableCell>
                  <TableCell>
                    {cargo.salario != null
                      ? Number(cargo.salario).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={cargo.ativo ? "default" : "secondary"}>
                      {cargo.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => abrirEditar(cargo)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal Novo / Editar */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) fecharModal(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="break-words">
              {editId !== null ? "Editar Cargo" : "Novo Cargo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1">
              <Label>Emitente *</Label>
              {user?.role === "cliente" ? (
                <Input value={user.emitenteId ? `Emitente ${user.emitenteId}` : ""} disabled className="bg-muted" />
              ) : (
                <Select
                  value={formEmitenteId ? String(formEmitenteId) : ""}
                  onValueChange={(value) => setFormEmitenteId(Number(value))}
                  disabled={editId !== null}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    {emitentes.map((emitente: any) => (
                      <SelectItem key={emitente.id} value={String(emitente.id)}>
                        {emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Código</Label>
                <Input
                  value={form.codigo}
                  onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value }))}
                  placeholder="Ex: GER-001"
                />
              </div>
              <div className="space-y-1">
                <Label>Nome *</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                  placeholder="Nome do cargo"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Setor</Label>
                <Input
                  value={form.setor}
                  onChange={(e) => setForm((f) => ({ ...f, setor: e.target.value }))}
                  placeholder="Ex: Comercial"
                />
              </div>
              <div className="space-y-1">
                <Label>Salário (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.salario}
                  onChange={(e) => setForm((f) => ({ ...f, salario: e.target.value }))}
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Descritivo do Cargo</Label>
              <Textarea
                value={form.descritivo}
                onChange={(e) => setForm((f) => ({ ...f, descritivo: e.target.value }))}
                placeholder="Descrição das responsabilidades e atribuições..."
                rows={3}
              />
            </div>
            <div className="space-y-1">
              <Label>Descritivo de Benefícios</Label>
              <Textarea
                value={form.descritivo_beneficios}
                onChange={(e) => setForm((f) => ({ ...f, descritivo_beneficios: e.target.value }))}
                placeholder="Descrição dos benefícios associados ao cargo..."
                rows={3}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.ativo}
                onCheckedChange={(v) => setForm((f) => ({ ...f, ativo: v }))}
              />
              <Label>Cargo ativo</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={fecharModal} disabled={isBusy}>
                <X className="w-4 h-4 mr-1" /> Cancelar
              </Button>
              <Button onClick={handleSalvar} disabled={isBusy}>
                {isBusy ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
